import { useMemo } from "react";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useDashboard } from "../../../Hooks/dealerRoles/useDashboard";
import DashboardHeader from "../../../Components/Dashboard/dealerRoles/DashboardHeader";
import ScopeSwitch from "../../../Components/Dashboard/dealerRoles/ScopeSwitch";
import OverviewStats from "../../../Components/Dashboard/dealerRoles/OverviewStats";
import QuotaSummaryCard from "../../../Components/Dashboard/dealerRoles/QuotaSummaryCard";
import { useGetDealerRolesOverviewQuery } from "../../../store/dealerRoles/firmApi";
import ConsumptionSection from "../../../Components/Dashboard/ConsumptionSection";
import ConsumptionGraphSection from "../../../Components/Dashboard/ConsumptionGraphSection";
import ConsumptionBreakdownCard from "../../../Components/Dashboard/storageRoles/ConsumptionBreakdownCard";
import { FaExclamationTriangle } from "react-icons/fa";
import { groupEspressoBased } from "../../../utils/dashboardUtils";

const DEALER_DASHBOARD_TYPE = {
  type: "Firma",
  text: "Tüketimler",
  graphText: "Son 6 Ay Tüketim Hareketi",
  warn: "Henüz tüketim verisi yok.",
};

function DealerDashboardPage() {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();

  const canSeeGraph = hasPermission("dashboard.graph");
  const canSeeScope = hasPermission("dashboard.scope");
  const canSeeQuota = hasPermission("dashboard.quota");

  const {
    scope,
    handleScopeChange,
    activeConsumption,
    handleClickConsumption,
    consumptionLoading,
    connectionError,
    monthlyConsumption,
    getConsumptionData,
    performance,
    deviceBreakdown,
    firmBreakdown,
  } = useDashboard(auth.accessToken);

  // Üst durum kutuları — scope'tan bağımsız, tüm bayi ağacının özeti.
  // subDealerCount ayrıca kapsam seçicisini şekillendirir (alt bayi yoksa
  // "Bayilerim" segmenti gizlenir).
  const { data: overviewRes, isLoading: overviewLoading } =
    useGetDealerRolesOverviewQuery(
      { token: auth.accessToken },
      // Cihaza kota verme/geri alma başka API diliminde (tag ile tazelenmiyor);
      // dashboard her açılışta güncel kotayı çeksin.
      { skip: !auth.accessToken, refetchOnMountOrArgChange: true }
    );
  const overview = overviewRes?.data ?? null;

  const rawConsumption = getConsumptionData();
  const groupedConsumption =
    rawConsumption != null ? groupEspressoBased(rawConsumption) : null;

  const groupedMonthlyConsumption = useMemo(() => {
    if (!Array.isArray(monthlyConsumption)) return [];
    return monthlyConsumption.map((log) => ({
      ...log,
      productConsument: groupEspressoBased(log.productConsument),
    }));
  }, [monthlyConsumption]);

  return (
    <div className="sm:py-2 sm:px-4">
      <div className="flex flex-col h-[calc(100vh-64px)] sm:h-[calc(100vh-16px)]">
        {/* Header */}
        <div className="shrink-0 z-30 relative">
          <DashboardHeader isLoading={consumptionLoading} />
        </div>

        {/* Scrollable Content */}
        <div
          className="flex-1 overflow-y-auto overflow-x-hidden"
          style={{ minHeight: 0 }}
        >
          {connectionError && !groupedConsumption ? (
            // Maskot görseli kaldırıldı (bayi dashboard'unda maskot
            // kullanılmıyor); yerine kart diliyle uyumlu sade bir durum kutusu.
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-3 px-4">
              <div className="p-4 bg-red-50 border border-red-100 rounded-2xl">
                <FaExclamationTriangle className="w-7 h-7 text-red-500" />
              </div>
              <p className="text-base sm:text-lg font-SemiBold text-gray-700">
                Veriler yüklenemedi
              </p>
              <p className="text-sm text-gray-500 text-center">
                Bağlantı sorunu oluştu. Sayfayı yenileyerek tekrar deneyin.
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-6 py-3 sm:py-4 pb-4 sm:pb-12">
              {/* Üst durum kutuları — ağaç özeti (scope'tan bağımsız) */}
              <OverviewStats data={overview} isLoading={overviewLoading} />

              {/* Bayi kotası — bende / müşteri ve alt bayilerimde (#110, #176) */}
              {canSeeQuota && (
                <QuotaSummaryCard
                  quota={overview?.quota}
                  isLoading={overviewLoading}
                />
              )}

              <div className="relative flex flex-col lg:flex-row lg:items-stretch lg:h-[520px] xl:h-[560px] gap-3 sm:gap-4 z-10 px-2 sm:px-0">
                {canSeeGraph && (
                  <ConsumptionGraphSection
                    dashboardConsumentIsLoading={consumptionLoading}
                    dashboardConsumentIsFetching={false}
                    dashboardType={DEALER_DASHBOARD_TYPE}
                    warn={DEALER_DASHBOARD_TYPE.warn}
                    monthlyConsumption={groupedMonthlyConsumption}
                  />
                )}

                <ConsumptionSection
                  dashboardType={DEALER_DASHBOARD_TYPE}
                  dashboardTypes={[DEALER_DASHBOARD_TYPE]}
                  setDashboardType={() => { }}
                  activeConsumption={activeConsumption}
                  handleClickConsumption={handleClickConsumption}
                  consumption={groupedConsumption}
                  // Performans (yüzde iyi/kötü rozetleri) depo dashboard'undaki
                  // ile aynı payload — socket getConsumptionPerformanceData.
                  performance={performance}
                  warn={DEALER_DASHBOARD_TYPE.warn}
                  isLoading={consumptionLoading}
                  isFetching={false}
                  isAdmin={false}
                  // Bayide SATIŞ yok: satış analizi fatura yüklemesinden
                  // üretiliyor, fatura akışı depo tarafında. Varsayılan true
                  // olduğu için ürün kartlarında boş "Satış:" rozeti çıkıyordu.
                  canSeeBillDetails={false}
                  incomes={null}
                  salesQuantity={null}
                  belowFilters={
                    <ScopeSwitch
                      scope={scope}
                      onScopeChange={handleScopeChange}
                      canSeeScope={canSeeScope}
                      subDealerCount={overview?.subDealerCount ?? null}
                    />
                  }
                />

              </div>

              {/* Cihaz + Firma tüketim kırılımları — depo dashboard'uyla aynı
                  bileşen ve aynı payload (deviceBreakdown/firmBreakdown zaten
                  bayi kapsamına göre filtrelenmiş geliyor). */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 z-0 px-2 sm:px-0">
                <ConsumptionBreakdownCard
                  mode="devices"
                  period={activeConsumption}
                  consumption={groupedConsumption}
                  performance={performance?.[activeConsumption]}
                  breakdown={deviceBreakdown}
                  isLoading={consumptionLoading}
                />
                <ConsumptionBreakdownCard
                  mode="firms"
                  period={activeConsumption}
                  consumption={groupedConsumption}
                  performance={performance?.[activeConsumption]}
                  breakdown={firmBreakdown}
                  isLoading={consumptionLoading}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DealerDashboardPage;
